<script lang="ts">
  import type { Snippet } from "svelte";

  /**
   * The tube's picture. One component so the look is identical whether you are sitting at the
   * station in 3D or looking at the screen alone (`?view=screen`, and phones).
   *
   * Layers: phosphor text (DOM, real focus/keyboard) → glass (scanlines, shadow mask, vignette)
   * → line pairing → grain → hum bar → sync roll → tear → dropouts. Everything reads one number:
   * `signal` (1 = pristine).
   *
   * The tube is old regardless of the signal: it hums, it pairs its lines, it breathes, and every
   * few seconds it loses lock for a frame or two. A bad signal only makes it do all of that more.
   * Rule for every artefact here: it may make the picture look tired, never make it hard to read —
   * so the instabilities are short (under 200 ms), small (a few pixels), and never overlap for long.
   */
  let { signal = 1, fill = false, station = "reactor", children }:
    { signal?: number; fill?: boolean; station?: string; children: Snippet } = $props();

  /**
   * Every station runs a different panel. The bridge has the good amber tube; the breaker bay has a
   * cheap, harsh one that flickers; storage is a pale blue-white inventory terminal. Colour and glass
   * character, never legibility — you should be able to tell where a crewmate is from their feed.
   */
  const PANELS: Record<string, { phos: string; glow: string; tube: string; scan: number; flick: number; hold: number }> = {
    bridge:      { phos: "#ffc46b", glow: "255, 195, 120", tube: "#150d03", scan: 0.8, flick: 0.6, hold: 0.7 },
    reactor:     { phos: "#9cff9c", glow: "120, 255, 140", tube: "#04140a", scan: 1.0, flick: 1.0, hold: 1.0 },
    breaker_bay: { phos: "#ffb257", glow: "255, 170, 90",  tube: "#140a02", scan: 1.5, flick: 2.0, hold: 1.8 },
    storage:     { phos: "#bfe6ff", glow: "170, 220, 255", tube: "#04101a", scan: 1.2, flick: 0.9, hold: 0.9 },
    gangway:     { phos: "#9cff9c", glow: "120, 255, 140", tube: "#04140a", scan: 1.0, flick: 1.2, hold: 1.3 },
  };
  const panel = $derived(PANELS[station] ?? PANELS.reactor!);

  const reducedMotion = typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
  /**
   * At a clean signal the picture filter is `saturate(1) brightness(1)` — a no-op that still costs a
   * flattened layer. Hand the browser `none` instead, the same way the station view does with blur.
   */
  const crtFilter = $derived(signal > 0.995
    ? "none"
    : `saturate(${(1 - (1 - signal) * 0.35).toFixed(3)}) brightness(${(1 - (1 - signal) * 0.18).toFixed(3)})`);

  // The schedulers below must not restart every time the signal ticks, so they read it through a
  // plain object rather than through the reactive prop.
  const sig = { v: 1, hold: 1 };
  $effect(() => { sig.v = signal; sig.hold = panel.hold; });

  let dropout = $state(false);
  let slip = $state(0);      // vertical hold slipping: the frame jumps and the wrap shows
  let bend = $state(0);      // horizontal sync bending: the raster leans for a frame or two
  let tearTop = $state(-1);  // a torn line, somewhere down the picture

  const rand = (min: number, max: number) => min + Math.random() * (max - min);

  $effect(() => {
    if (reducedMotion) return;
    const t = window.setInterval(() => {
      if (Math.random() < (1 - sig.v) * 0.12) { dropout = true; window.setTimeout(() => { dropout = false; }, rand(60, 200)); }
    }, 700);
    return () => window.clearInterval(t);
  });

  // Loss of lock. Idle cadence is a handful of seconds even on a clean feed — the monitor is the
  // problem, not the transmission — and it tightens as the signal rots.
  $effect(() => {
    if (reducedMotion || !fill) return;
    let timer = 0;
    const next = () => {
      const hurry = (0.3 + sig.v * 0.7) / Math.max(0.4, sig.hold);
      timer = window.setTimeout(fire, rand(3800, 11000) * hurry);
    };
    const fire = () => {
      const r = Math.random();
      if (r < 0.42) { slip = Math.random() < 0.75 ? 1 : 2; window.setTimeout(() => { slip = 0; }, rand(90, 190)); }
      else if (r < 0.76) { bend = Math.random() < 0.5 ? -1 : 1; window.setTimeout(() => { bend = 0; }, rand(70, 150)); }
      else { tearTop = rand(6, 84); window.setTimeout(() => { tearTop = -1; }, rand(60, 130)); }
      next();
    };
    next();
    return () => window.clearTimeout(timer);
  });
</script>

<div class="screen" class:fill class:dropout class:slipping={slip > 0}
  style="--signal: {signal.toFixed(3)}; --phos: {panel.phos}; --glow: {panel.glow}; --tube: {panel.tube};
         --scan: {panel.scan}; --flick: {panel.flick}; --hold: {panel.hold};
         --slip: {slip * -9}px; --bend: {(bend * 0.42).toFixed(2)}deg; --bendx: {bend * 1.8}px;
         --crt-filter: {crtFilter}">
  <div class="crt-content">{@render children()}</div>
  <div class="glass" aria-hidden="true"></div>
  <div class="pair" aria-hidden="true"></div>
  <div class="grain" aria-hidden="true"></div>
  <div class="hum" aria-hidden="true"></div>
  <div class="roll" aria-hidden="true"></div>
  {#if tearTop >= 0}<div class="tear" aria-hidden="true" style="top: {tearTop.toFixed(1)}%"></div>{/if}
  <div class="wrap" aria-hidden="true"></div>
  <div class="bulge" aria-hidden="true"></div>
</div>

<style>
  .screen {
    --signal: 1;
    --bad: calc(1 - var(--signal));
    position: relative; isolation: isolate;
    width: 100%; height: 100%; overflow: hidden;
    background: radial-gradient(120% 100% at 50% 45%, var(--tube, #04140a) 0%, #010703 72%, #000 100%);
    color: var(--phos, #9cff9c); letter-spacing: 0.04em;
    border-radius: 42px / 52px;
    box-shadow: inset 0 0 70px rgba(0, 0, 0, 0.85), inset 0 0 10px rgba(var(--glow, 120, 255, 140), 0.16);
  }
  /* The supply sags: the whole picture breathes on a period that never lines up with the flicker.
     Screen-only — see the note on `.pair`: animating a filter on the element that every blended
     layer sits inside is fine on a flat page and asking for trouble inside a matrix3d subtree. */
  .screen.fill { animation: breathe 11.9s ease-in-out infinite; }
  /* Alone in the browser the tube fills the viewport, with a heavier tube edge to sell the glass. */
  .screen.fill {
    position: fixed; inset: 0; width: 100dvw; height: 100dvh; border-radius: 0;
    box-shadow: inset 0 0 220px rgba(0, 0, 0, 0.9), inset 0 0 26px rgba(156, 255, 156, 0.10);
  }
  /* Phosphor bloom: layered shadows rather than a filter — cheap, and it spills like a real tube.
     The transform carries the sync faults; it is the raster that moves, not the glass in front of it. */
  .crt-content {
    position: relative; z-index: 1; height: 100%; padding: 16px 20px; overflow-y: auto; overflow-x: hidden;
    scrollbar-width: thin; scrollbar-color: rgba(156, 255, 156, 0.35) transparent;
    text-shadow:
      0 0 2px currentColor,
      0 0 10px rgba(var(--glow, 120, 255, 140), calc(0.42 - var(--bad) * 0.12)),
      0 0 26px rgba(var(--glow, 120, 255, 140), calc(0.20 + var(--bad) * 0.10));
    filter: var(--crt-filter, none);
    transform: translate3d(var(--bendx, 0px), var(--slip, 0px), 0) skewX(var(--bend, 0deg));
    transition: transform 40ms linear;
  }
  /* Glass: scanlines + shadow mask + vignette. Multiplies, so it only ever darkens. */
  .glass {
    position: absolute; inset: 0; z-index: 2; pointer-events: none; border-radius: inherit;
    mix-blend-mode: multiply;
    background:
      repeating-linear-gradient(0deg, rgba(0, 0, 0, calc((0.20 + var(--bad) * 0.30) * var(--scan, 1))) 0 1px, transparent 1px 3px),
      repeating-linear-gradient(90deg, rgba(255, 40, 40, 0.05) 0 1px, rgba(40, 255, 40, 0.05) 1px 2px, rgba(40, 40, 255, 0.05) 2px 3px),
      radial-gradient(125% 105% at 50% 50%, transparent 52%, rgba(0, 0, 0, 0.75) 100%);
    animation: flicker 130ms steps(2, end) infinite;
  }
  /*
   * Line pairing — the tube's field rate is not quite stable, so alternate line pairs sit closer
   * together and the picture shimmers. This is most of what "unstable Hz" actually looks like.
   * Screen-only: in the 3D station the tube panel is downscaled and a 4 px pitch would moiré.
   */
  .pair { display: none; }
  .screen.fill .pair {
    display: block; position: absolute; inset: -4px; z-index: 2; pointer-events: none;
    mix-blend-mode: multiply; opacity: calc(0.5 + var(--bad) * 0.5);
    background: repeating-linear-gradient(0deg, rgba(0, 0, 0, 0.13) 0 1px, transparent 1px 4px);
    animation: pairing 67ms steps(2, end) infinite;
  }
  /* Alone in the browser the picture needs the tube's inset, or it reads as a web page. */
  .screen.fill .crt-content { padding: clamp(1rem, 3vw, 2.6rem) clamp(1rem, 4vw, 3.2rem);
    /* A worn tube never quite converges its guns: a hair of colour on either side of every stroke. */
    text-shadow:
      -0.6px 0 rgba(255, 90, 90, calc(0.10 + var(--bad) * 0.16)),
      0.6px 0 rgba(90, 150, 255, calc(0.09 + var(--bad) * 0.14)),
      0 0 2px currentColor,
      0 0 10px rgba(var(--glow, 120, 255, 140), calc(0.42 - var(--bad) * 0.12)),
      0 0 26px rgba(var(--glow, 120, 255, 140), calc(0.20 + var(--bad) * 0.10));
  }
  .screen.fill .glass { background:
      repeating-linear-gradient(0deg, rgba(0, 0, 0, calc(0.16 + var(--bad) * 0.26)) 0 1px, transparent 1px 3px),
      repeating-linear-gradient(90deg, rgba(255, 40, 40, 0.04) 0 1px, rgba(40, 255, 40, 0.04) 1px 2px, rgba(40, 40, 255, 0.04) 2px 3px),
      radial-gradient(145% 120% at 50% 50%, transparent 58%, rgba(0, 0, 0, 0.8) 100%); }
  /* Grain: one baked turbulence tile, translated per frame. Screens, so it only ever lifts. */
  .grain {
    position: absolute; inset: -20%; z-index: 3; pointer-events: none;
    mix-blend-mode: screen; opacity: calc(0.05 + var(--bad) * 0.30);
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='140' height='140' filter='url(%23n)'/%3E%3C/svg%3E");
    animation: grain 420ms steps(6, end) infinite;
  }
  /* Mains hum bar: the frame rate and the ship's supply are not locked to each other, so a soft
     dark band crawls down the picture forever. Always there, signal or no signal. */
  .hum { display: none; }
  .screen.fill .hum {
    display: block; position: absolute; left: 0; right: 0; height: 46%; z-index: 3; pointer-events: none;
    mix-blend-mode: multiply; opacity: calc((0.5 + var(--bad) * 0.5) * var(--hold, 1));
    background: linear-gradient(180deg, transparent, rgba(0, 0, 0, 0.16) 44%, rgba(0, 0, 0, 0.22) 56%, transparent);
    animation: hum 6.7s linear infinite;
  }
  /* Sync roll: a bright band drifting up the tube, stronger as the signal rots. */
  .roll {
    position: absolute; left: 0; right: 0; height: 22%; z-index: 4; pointer-events: none;
    mix-blend-mode: screen; opacity: calc(0.07 + var(--bad) * 0.5);
    background: linear-gradient(180deg, transparent, rgba(var(--glow, 180, 255, 200), 0.10) 45%, rgba(var(--glow, 220, 255, 230), 0.18) 55%, transparent);
    animation: roll calc(3s + var(--signal) * 9s) linear infinite;
  }
  /* A torn line: the beam restarts early and one band of the picture is thrown sideways. */
  .tear {
    position: absolute; left: 0; right: 0; height: 9px; z-index: 5; pointer-events: none;
    mix-blend-mode: screen;
    background: linear-gradient(180deg, rgba(var(--glow, 180, 255, 200), 0.22), rgba(0, 0, 0, 0.5) 55%, rgba(var(--glow, 180, 255, 200), 0.12));
    transform: translateX(-6px) scaleY(1.4);
  }
  /* The wrap: while the frame is slipping, the bottom of the picture is the top of the next one. */
  .wrap {
    position: absolute; left: 0; right: 0; bottom: 0; height: 12px; z-index: 5; pointer-events: none;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0.55));
    opacity: 0; transition: opacity 40ms linear;
  }
  .screen.slipping .wrap { opacity: 1; }
  /* Convex glass: a soft specular off the top-left, a rim along the top edge, and corner fall-off.
     The tube reads as bulged from the light on it — the picture itself stays flat and legible. */
  .bulge {
    position: absolute; inset: 0; z-index: 6; pointer-events: none; border-radius: inherit;
    mix-blend-mode: screen;
    background:
      radial-gradient(58% 42% at 26% 16%, rgba(215, 255, 235, 0.11), rgba(215, 255, 235, 0.04) 45%, transparent 68%),
      radial-gradient(90% 12% at 50% 2%, rgba(200, 255, 220, 0.10), transparent 70%),
      linear-gradient(102deg, transparent 34%, rgba(210, 255, 230, 0.035) 41%, transparent 47%);
  }
  .screen.fill .bulge { background:
      radial-gradient(52% 38% at 22% 12%, rgba(215, 255, 235, 0.07), transparent 66%),
      radial-gradient(90% 10% at 50% 1%, rgba(200, 255, 220, 0.06), transparent 70%),
      /* Flagging: the first lines of the frame bend and bloom while the sync settles. */
      linear-gradient(180deg, rgba(215, 255, 235, 0.05), transparent 7%); }
  /* Dropout: the picture loses lock for a frame or two. */
  .screen.dropout .crt-content { opacity: 0.25; transform: translateY(-2px) scaleY(1.02); }
  .screen.dropout .grain { opacity: 0.85; }
  @keyframes flicker { 0%, 100% { opacity: 1; } 50% { opacity: calc(1 - (0.04 + var(--bad) * 0.10) * var(--flick, 1)); } }
  @keyframes pairing { 0% { transform: translateY(0); } 100% { transform: translateY(2px); } }
  @keyframes breathe { 0%, 100% { filter: brightness(1); } 37% { filter: brightness(1.035); } 68% { filter: brightness(0.962); } }
  @keyframes grain { to { transform: translate3d(9%, 6%, 0); } }
  @keyframes hum { from { top: -46%; } to { top: 100%; } }
  @keyframes roll { from { top: 105%; } to { top: -25%; } }
  @media (prefers-reduced-motion: reduce) {
    /* `.screen.fill` beats a bare `.screen` on specificity, so name it here or the breathe survives. */
    .screen, .screen.fill, .glass, .grain, .roll, .hum { animation: none; }
    .roll, .tear, .wrap, .hum, .screen.fill .hum, .screen.fill .pair { display: none; }
    .crt-content { transition: none; }
  }
</style>
